import { z } from 'zod'

export const GUEST_SIDES = ['bride', 'groom', 'shared'] as const
export const GUEST_RELATIONSHIPS = [
  'close-family',
  'extended-family',
  'close-friends',
  'other',
] as const
export const GUEST_DAY_PARTS = ['all-day', 'evening'] as const
export const GUEST_ATTENDANCES = ['invited', 'waiting', 'yes', 'no'] as const
export const GUEST_DIETS = ['vegetarian', 'vegan', 'lactose-free', 'gluten-free'] as const
export const GUEST_RSVP_STATUSES = ['not_replied', 'replied'] as const
/** Where the party's latest reply came from. */
export const GUEST_RSVP_SOURCES = ['personal-link', 'shared-link'] as const

export type GuestSide = (typeof GUEST_SIDES)[number]
export type GuestRelationship = (typeof GUEST_RELATIONSHIPS)[number]
export type GuestDayPart = (typeof GUEST_DAY_PARTS)[number]
export type GuestAttendance = (typeof GUEST_ATTENDANCES)[number]
export type GuestDiet = (typeof GUEST_DIETS)[number]
export type GuestRsvpStatus = (typeof GUEST_RSVP_STATUSES)[number]
export type GuestRsvpSource = (typeof GUEST_RSVP_SOURCES)[number]

const short = z.string().trim().max(300)
const text = z.string().trim().max(5000)

/** A person on an invitation. Only a +1 may stay unnamed. */
export const guestInputSchema = z
  .object({
    /** An existing person keeps its id; a new one omits it. */
    id: z.uuid().optional(),
    firstName: short,
    lastName: short,
    plusOne: z.boolean(),
    isChild: z.boolean(),
    age: z.number().int().min(0).max(17).nullable(),
    attendance: z.enum(GUEST_ATTENDANCES),
    diets: z
      .array(z.enum(GUEST_DIETS))
      .max(GUEST_DIETS.length)
      .refine((diets) => new Set(diets).size === diets.length),
    notes: text,
  })
  .refine((guest) => guest.plusOne || guest.firstName !== '', { path: ['firstName'] })

/** An invitation with the persons it invites, as the planner saves it. */
export const guestPartyInputSchema = z.object({
  name: short,
  side: z.enum(GUEST_SIDES),
  relationship: z.enum(GUEST_RELATIONSHIPS),
  dayPart: z.enum(GUEST_DAY_PARTS),
  email: z.email().max(300).or(z.literal('')),
  phone: z.string().trim().max(50),
  address: short,
  accommodation: z.boolean(),
  accommodationNote: short,
  transport: z.boolean(),
  transportNote: short,
  notes: text,
  persons: z
    .array(guestInputSchema)
    .min(1)
    .max(50)
    .refine((persons) => {
      const ids = persons.flatMap((person) => (person.id ? [person.id] : []))
      return new Set(ids).size === ids.length
    }),
})

export const createGuestPartiesSchema = z.object({
  parties: z.array(guestPartyInputSchema).min(1).max(500),
})

/** Takes a reply from the shared website link into a party, or into a new one. */
export const importGuestReplySchema = z.object({
  entryId: z.uuid(),
  partyId: z.uuid().nullable(),
})

/** A reply through the party's personal link: an answer for every person in the party. */
export const personalReplySchema = z.object({
  persons: z
    .array(
      z.object({
        id: z.uuid(),
        coming: z.boolean(),
        diets: guestInputSchema.shape.diets,
        /** Names an unnamed +1; ignored for everyone else. */
        firstName: short,
      }),
    )
    .min(1)
    .max(50)
    .refine((persons) => new Set(persons.map((person) => person.id)).size === persons.length),
  accommodation: z.boolean(),
  transport: z.boolean(),
  message: z.string().trim().max(2000),
})

export type GuestInput = z.infer<typeof guestInputSchema>
export type GuestPartyInput = z.infer<typeof guestPartyInputSchema>
export type CreateGuestPartiesInput = z.infer<typeof createGuestPartiesSchema>
export type ImportGuestReplyInput = z.infer<typeof importGuestReplySchema>
export type PersonalReplyInput = z.infer<typeof personalReplySchema>
